import React, { useState, useEffect, useContext } from "react";
import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";

import { UserContext } from "../../context/UserContext";
import { db } from "../../services/firebaseConfig";
import css from "./Account.module.css";

export const AccountPage = () => {
  const { user, setUser, userData, setUserData } = useContext(UserContext);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [status, setStatus] = useState("");
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // New: confirmation modal for account deletion
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  useEffect(() => {
    setDisplayName(userData?.displayName || "");
    setEmail(userData?.email || "");
  }, [userData]);

  if (!user) return <p>Carregando...</p>;

  async function handleUpdate(e) {
    e.preventDefault();
    setStatus("");

    const emailChanged = email !== user.email;
    const passwordChanged = password.trim() !== "";

    // Se for necessário reautenticar, abre o modal e pausa a ação
    if ((emailChanged || passwordChanged) && !showReauthModal) {
      setShowReauthModal(true);
      return;
    }

    setLoading(true);
    try {
      // Atualiza displayName
      if (displayName !== user.displayName) {
        await updateProfile(user, { displayName });
      }

      // Atualiza email
      if (emailChanged) {
        await updateEmail(user, email);
      }

      // Atualiza senha
      if (passwordChanged) {
        await updatePassword(user, password);
      }

      // Atualiza Firestore (userData)
      const userDocRef = doc(db, "userData", user.uid);
      await updateDoc(userDocRef, {
        displayName,
        email,
        updatedAt: new Date(),
      });

      setUser({ ...user, displayName, email });
      setUserData({ ...userData, displayName, email });

      setStatus("Informações atualizadas com sucesso ✅");
      setPassword("");
      setCurrentPassword("");
      setShowReauthModal(false);
    } catch (error) {
      console.error("Erro:", error);
      let message = error.message;
      if (error.code === "auth/wrong-password")
        message = "Senha atual incorreta.";
      else if (error.code === "auth/requires-recent-login")
        message = "Você precisa fazer login novamente.";
      setStatus(`Erro: ${message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleReauthenticate(e) {
    e.preventDefault();
    setStatus("Verificando credenciais...");
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      setStatus("Reautenticação concluída. Salvando alterações...");
      setShowReauthModal(false);
      // continue the update flow
      const fakeEvent = { preventDefault: () => {} };
      await handleUpdate(fakeEvent);
    } catch (error) {
      console.error("Erro ao reautenticar:", error);
      setStatus("Senha incorreta. Tente novamente.");
    }
  }

  // Apaga somente os dados de personalidade (LGPD-friendly)
  async function handleDeletePersonality() {
    if (
      !window.confirm(
        "Excluir seus dados de personalidade irá remover avatar, ferramentas e resultados. Deseja continuar?"
      )
    )
      return;

    setLoading(true);
    setStatus("Apagando dados de personalidade...");
    try {
      const userDocRef = doc(db, "userData", user.uid);
      const patch = {
        personalityTraits: null,
        tools: {},
        updatedAt: new Date(),
      };
      await updateDoc(userDocRef, patch);

      // atualiza contexto local
      setUserData((prev) => ({ ...(prev || {}), ...patch }));

      setStatus("Dados de personalidade removidos.");
    } catch (err) {
      console.error("Erro ao apagar personalidade:", err);
      setStatus("Erro ao apagar dados. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  // Excluir conta: tenta apagar Firestore e depois o usuário do Auth (requer login recente)
  async function handleDeleteAccount() {
    setLoading(true);
    setStatus("Iniciando processo de exclusão de conta...");

    try {
      const userDocRef = doc(db, "userData", user.uid);

      // 0) Auditoria antes de apagar
      try {
        await updateDoc(userDocRef, {
          deletionRequestedAt: new Date().toISOString(),
          deletionRequestedBy: user.uid,
        });
      } catch (auditErr) {
        console.warn(
          "Falha ao registrar auditoria, prosseguindo mesmo assim:",
          auditErr
        );
        // Mesmo se falhar, não devemos impedir a exclusão da conta
      }

      // 1) Apaga documento Firestore (userData)
      try {
        await deleteDoc(userDocRef);
      } catch (fireErr) {
        console.warn(
          "Não foi possível apagar doc diretamente, marcando deletedAt:",
          fireErr
        );
        // Se não conseguiu apagar, ao menos marcar
        await updateDoc(userDocRef, {
          deletedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      // 2) Tenta apagar o usuário do Firebase Auth
      try {
        await deleteUser(user);
        setUser(null);
        setUserData(null);
        setStatus("Conta excluída com sucesso.");
      } catch (authErr) {
        console.error("Erro ao apagar usuário do Auth:", authErr);

        if (authErr.code === "auth/requires-recent-login") {
          setStatus(
            "Para excluir a conta, confirme sua senha. Abrindo verificação…"
          );
          setShowReauthModal(true);
        } else {
          setStatus(
            "Conta removida do banco de dados, mas falhou ao remover credenciais de login. Contate o suporte."
          );
        }
      }
    } catch (err) {
      console.error("Erro no processo de exclusão:", err);
      setStatus("Erro ao excluir conta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  if (!userData) return <p>Carregando informações do usuário...</p>;

  return (
    <div className={css.accountContainer}>
      <h2 className={css.accountTitle}>Minha Conta</h2>

      <form onSubmit={handleUpdate} className="accountForm">
        <div className={css.formField}>
          <label>Nome de exibição</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <div className={css.formField}>
          <label>E-mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={css.formField}>
          <label>Nova senha</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Deixe em branco se não for alterar"
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <button type="submit" className={css.btnPrimary} disabled={loading}>
            {loading ? "Salvando..." : "Salvar alterações"}
          </button>

          <button
            type="button"
            className={css.btnSecondary}
            onClick={() => {
              // redirecionar para página de perfil (opcional)
            }}
            disabled={loading}
          >
            Cancelar
          </button>
        </div>
      </form>

      {status && <p className={css.statusMessage}>{status}</p>}

      {/* Reauth modal (usado também no fluxo de exclusão se reauth necessário) */}
      {showReauthModal && (
        <div className={css.modalOverlay}>
          <div className={css.modal}>
            <h3>Confirme sua senha</h3>
            <p>
              Para alterar e-mail, senha ou excluir sua conta, confirme sua
              senha atual.
            </p>

            <form onSubmit={handleReauthenticate}>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Senha atual"
                required
              />
              <div className={css.modalButtons}>
                <button type="submit" className={css.btnPrimary}>
                  Confirmar
                </button>
                <button
                  type="button"
                  className={css.btnSecondary}
                  onClick={() => {
                    setShowReauthModal(false);
                    setCurrentPassword("");
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Privacy / account actions */}
      <div style={{ marginTop: 24 }}>
        <div className={css.card}>
          <h3>Privacidade</h3>
          <p className={css.muted}>
            Apague apenas os dados de personalidade sem apagar sua conta.
          </p>
          <div style={{ marginTop: 8 }}>
            <button
              className={css.dangerBtn}
              onClick={handleDeletePersonality}
              disabled={loading}
            >
              Excluir dados de personalidade
            </button>
          </div>
        </div>

        <div className={css.card} style={{ marginTop: 12 }}>
          <h3>Excluir Conta</h3>
          <p className={css.muted}>
            Excluir conta remove seus dados e credenciais. A ação é
            irreversível.
          </p>
          <div style={{ marginTop: 8 }}>
            {/* Open confirmation modal first */}
            <button
              className={css.dangerBtnOutline}
              onClick={() => setShowDeleteConfirmModal(true)}
              disabled={loading}
            >
              Excluir Conta
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation modal before account deletion */}
      {showDeleteConfirmModal && (
        <div className={css.modalOverlay}>
          <div className={css.modal}>
            <h3>Confirmar exclusão de conta</h3>
            <p>
              Esta ação <strong>é irreversível</strong>. Todos os seus dados
              serão removidos permanentemente, incluindo o documento de usuário
              e as credenciais de acesso. Em alguns casos, pode ser necessária a
              confirmação da sua senha (reautenticação) para concluir a
              exclusão.
            </p>

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                className={css.btnSecondary}
                onClick={() => setShowDeleteConfirmModal(false)}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                className={css.dangerBtn}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  // start deletion flow
                  handleDeleteAccount();
                }}
                disabled={loading}
              >
                Sim, excluir minha conta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
